/**
 * Copyright © 2024 Delicious AI, LLC
 */

import { useLazyQuery } from '@apollo/client'
import styled from '@emotion/styled'
import CloseIcon from '@mui/icons-material/Close'
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteRenderOptionState,
  Checkbox,
  Chip,
  ListItem,
  Radio,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import useT from '@/i18n/useT'

const Autocomplete = styled(MuiAutocomplete)({
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'nowrap',
  },
}) as typeof MuiAutocomplete

const AutocompleteWithWrap = styled(MuiAutocomplete)({
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'wrap',
  },
}) as typeof MuiAutocomplete

export type Option = { label: string; value: string }

export type RemoteSearchableSelectProps<T extends Option[] | Option | null> = {
  multiselect: boolean
  query: any
  getOptions: (data: any) => Option[]
  getVariablesForQuery: (textInput: string) => any
  handleChange: (newValue: T) => void
  value: T
  label: string
  wrapTags?: boolean
  showSelectAll?: boolean
  error?: boolean
  helperText?: string | false
  disabled?: boolean
  required?: boolean
}

export const RemoteSearchableSelect = <T extends Option[] | Option | null>(
  props: React.PropsWithChildren<RemoteSearchableSelectProps<T>>,
) => {
  const {
    multiselect,
    query,
    label,
    getOptions,
    getVariablesForQuery,
    value,
    handleChange,
    wrapTags = false,
    showSelectAll = false,
    error,
    helperText,
    disabled,
    required = false,
  } = props

  const { t } = useT()

  const AutocompleteComponent = wrapTags ? AutocompleteWithWrap : Autocomplete
  const [searchString, setSearchString] = useState('')
  const [debouncedSearchString] = useDebounce(searchString, 500)

  const [getData, { data, loading }] = useLazyQuery(query)

  const allOptions = getOptions(data)

  const selectAllOption: Option = {
    label: t('remoteSearchableSelect.selectAll'),
    value: 'Select All',
  }

  const displayedOptions =
    multiselect && showSelectAll ? [selectAllOption, ...allOptions] : allOptions

  useEffect(() => {
    getData({ variables: getVariablesForQuery(debouncedSearchString) })
  }, [debouncedSearchString, getData, getVariablesForQuery])

  const defaultRenderOption = (
    label: string,
    state: AutocompleteRenderOptionState,
  ) => {
    return (
      <>
        {multiselect && <Checkbox sx={{ mr: 1 }} checked={state.selected} />}
        {!multiselect && <Radio sx={{ mr: 1 }} checked={state.selected} />}
        <Typography>{label}</Typography>
      </>
    )
  }

  return (
    <AutocompleteComponent
      disabled={disabled}
      onChange={(_, newValue) => {
        if (multiselect && showSelectAll) {
          const newSelectedValues = newValue as Option[]
          const isSelectAllClicked = newSelectedValues.some(
            option => option.value === selectAllOption.value,
          )

          if (isSelectAllClicked) {
            const currentSelectedOptionValues = (value as Option[]).map(
              o => o.value,
            )
            const allOptionsValues = allOptions.map(o => o.value)

            const areAllCurrentlySelected =
              allOptionsValues.length > 0 &&
              allOptionsValues.every(optValue =>
                currentSelectedOptionValues.includes(optValue),
              )

            if (areAllCurrentlySelected) {
              handleChange([] as unknown as T)
            } else {
              handleChange(allOptions as T)
            }
          } else {
            const filteredNewValue = newSelectedValues.filter(
              option => option.value !== selectAllOption.value,
            )
            handleChange(filteredNewValue as T)
          }
        } else {
          handleChange(newValue as T)
        }
      }}
      value={value}
      options={displayedOptions}
      filterOptions={opt => opt}
      multiple={multiselect}
      loading={loading}
      sx={{
        minWidth: 200,
        maxWidth: 40000,
        flex: 1,
        display: 'flex',
      }}
      fullWidth
      disableListWrap
      disableCloseOnSelect
      isOptionEqualToValue={(option, val) => option.value === val.value}
      limitTags={2}
      slotProps={{
        paper: {
          variant: 'outlined',
        },
      }}
      renderInput={params => (
        <TextField
          label={label}
          {...params}
          value={searchString}
          onChange={e => setSearchString(e.target.value)}
          placeholder={t('remoteSearchableSelect.search')}
          sx={{ flex: 1, display: 'flex', minWidth: 250 }}
          error={error}
          helperText={helperText}
          disabled={disabled}
          required={required}
        />
      )}
      renderOption={(props, option, state) => {
        if (
          showSelectAll &&
          multiselect &&
          option.value === selectAllOption.value
        ) {
          const isAllSelected =
            allOptions.length > 0 &&
            allOptions.every(opt =>
              (value as Option[])?.some(
                selected => selected.value === opt.value,
              ),
            )
          return (
            <ListItem
              {...props}
              key={option.value}
              sx={theme => ({
                ':hover': {
                  backgroundColor: theme.palette.grey['200'],
                },
              })}
            >
              <Checkbox sx={{ mr: 1 }} checked={isAllSelected} />
              <Typography fontWeight="bold">{option.label}</Typography>
            </ListItem>
          )
        }

        return (
          <ListItem
            {...props}
            key={option.value}
            sx={theme => ({
              ':hover': {
                backgroundColor: theme.palette.grey['200'],
              },
            })}
          >
            {defaultRenderOption(option.label, state)}
          </ListItem>
        )
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue
          .map((option, index) => {
            if (showSelectAll && option.value === selectAllOption.value) {
              return null
            }

            const areAllOptionsSelected =
              allOptions.length > 0 &&
              (value as Option[]).length === allOptions.length &&
              allOptions.every(opt =>
                (value as Option[]).some(
                  selected => selected.value === opt.value,
                ),
              )

            if (areAllOptionsSelected) {
              if (index === 0) {
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key="all-selected-chip"
                    label={t('remoteSearchableSelect.allSelected')}
                    onDelete={() => handleChange([] as unknown as T)}
                    deleteIcon={<CloseIcon />}
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                )
              }
              return null
            }

            return (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            )
          })
          .filter(Boolean)
      }
    />
  )
}
